import * as Sentry from '@sentry/nextjs'
import {
  Auth,
  User,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  signOut,
} from 'firebase/auth'
import { useRouter } from 'next/router'
import React, { createContext, useEffect, useMemo, useState } from 'react'

import { allLocales } from '@/types/locales'

import firebaseApp from '@/lib/firebase/firebaseConfig'

type AuthState =
  | {
      auth: Auth
      user: User | null
      isLoading: false
    }
  | {
      auth: undefined
      user: undefined
      isLoading: true
    }

interface IAuthContext {
  authState: AuthState
}

export const AuthContext = createContext<IAuthContext>({
  authState: {
    auth: undefined,
    isLoading: true,
    user: undefined,
  },
})

interface AuthContextProviderProps {
  children: React.ReactNode
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const firebaseAuth = useMemo(() => {
    try {
      let isAppleUserAgent = false
      if (typeof window !== 'undefined') {
        const userAgent = window.navigator?.userAgent || ''
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent)
        const isIpadOS = /Macintosh/.test(userAgent) && 'ontouchend' in document
        isAppleUserAgent = isIOSDevice || isIpadOS
      }

      return initializeAuth(firebaseApp, {
        persistence: isAppleUserAgent ? browserSessionPersistence : indexedDBLocalPersistence,
      })
    } catch (error) {
      Sentry.captureException(new Error('initialiseAuth error', { cause: error }))
      return undefined
    }
  }, [])

  const [authState, setAuthState] = useState<AuthState>({
    auth: undefined,
    isLoading: true,
    user: undefined,
  })
  const router = useRouter()

  useEffect(() => {
    if (!firebaseAuth) {
      return
    }
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      setAuthState({
        auth: firebaseAuth,
        isLoading: false,
        user,
      })
    })

    return () => unsubscribe()
  }, [firebaseAuth])

  // Automatically log out anonymous users when they leave Zoe Plus checkout flow
  useEffect(() => {
    if (!router.isReady || !authState.auth || authState.isLoading || !authState.user?.isAnonymous) {
      return
    }

    const currentPath = router.asPath
    const allowedAnonymousPages = ['/zoe-plus/email', '/zoe-plus/checkout', '/zoe-plus/order-confirmation']
    const localisedAllowedAnonymousPages = allLocales.flatMap((locale) => {
      return allowedAnonymousPages.map((page) => `/${locale}${page}`)
    })

    const isAllowedPage = localisedAllowedAnonymousPages.some((allowedPath) => currentPath.startsWith(allowedPath))

    if (!isAllowedPage) {
      signOut(authState.auth).catch((error) => {
        console.error('Error logging out anonymous user:', error)
        Sentry.captureException(error, {
          extra: {
            currentPath,
            user: authState?.user,
          },
        })
      })
    }
  }, [router.isReady, router.asPath, authState])

  return <AuthContext.Provider value={{ authState }}>{children}</AuthContext.Provider>
}
